import { GroupSelectItemInterface } from '@components/common';

import {
	IDefaultOptions,
	IGroupSelectItem,
	IGroupSelectOptions,
} from '@components/common/GroupSelect/GroupSelect.types';

export const generateOptions = (options: Array<IGroupSelectOptions>, checked: boolean) => {
	const updateItems = (items: Array<GroupSelectItemInterface>): Array<GroupSelectItemInterface> => {
		return items.map((item) => ({
			...item,
			checked,
			items: item.items ? updateItems(item.items) : item.items,
		}));
	};

	return options.map((option) => ({
		...option,
		items: updateItems(option.items),
	}));
};

export const generateOptionsByIds = (
	options: Array<IGroupSelectOptions>,
	isChecked: boolean,
	ids: Array<string>,
	categoryName: string,
) => {
	const updateItems = (
		items: Array<GroupSelectItemInterface>,
		parentCategoryName: string,
		isTopLevel: boolean = false,
	): Array<GroupSelectItemInterface> => {
		return items.map(item => {
			if (item.category && item.items) {
				const shouldApplyToSubCategory = isTopLevel && parentCategoryName === categoryName;

				return {
					...item,
					items: updateItems(item.items, item.category.name, shouldApplyToSubCategory),
					...(shouldApplyToSubCategory ? { checked: isChecked } : {}),
				};
			}

			const checked =
				(isTopLevel || parentCategoryName === categoryName) && item.id && ids.includes(item.id)
					? { checked: isChecked }
					: {};

			return {
				...item,
				...checked,
			};
		});
	};

	return options.map(option => {
		const isTopLevel = option.category.name === categoryName;

		return {
			...option,
			items: updateItems(option.items || [], option.category.name, isTopLevel),
			...(isTopLevel ? { checked: isChecked } : {}),
		};
	});
};

export const generateListOfCheckedOptions = (
	options: Array<IGroupSelectOptions>,
): IGroupSelectItem[] => {
	const collectCheckedItems = (items: Array<IGroupSelectItem>): IGroupSelectItem[] => {
		return items.reduce<Array<IGroupSelectItem>>((checkedItems, item) => {
			if (item.items && item.items.length > 0) {
				return [...checkedItems, ...collectCheckedItems(item.items)];
			}
			if (item.checked) {
				checkedItems.push(item);
			}

			return checkedItems;
		}, []);
	};

	return options.reduce<Array<IGroupSelectItem>>((acc, option) => {
		const checkedValues = collectCheckedItems(option.items || []);

		return [...acc, ...checkedValues];
	}, []);
};

export const generateDynamicOptions = (
	options: Array<IGroupSelectOptions>,
	defaultOptions: Array<IDefaultOptions>,
): IGroupSelectOptions[] => {
	const updateItems = (items: Array<IGroupSelectItem>, parentCategoryName: string): Array<IGroupSelectItem> => {
		return items.map((item) => {
			const isChecked = defaultOptions.some((element) =>
				element.categoryName
					? element.id === item.id && element.categoryName === parentCategoryName
					: element.id === item.id,
			);

			return {
				...item,
				categoryName: parentCategoryName,
				checked: isChecked,
				items: item.items ? updateItems(item.items, item.category?.name || parentCategoryName) : item.items,
			};
		});
	};

	return options.map((option) => ({
		...option,
		items: updateItems(option.items, option.category.name),
	}));
};

export const generateListOfCheckedOptionsWithCategory = (options: Array<IGroupSelectOptions>) =>
	options.reduce<Array<IGroupSelectOptions>>((acc, option) => {
		const filterCheckedItems = (items: Array<IGroupSelectItem>): Array<IGroupSelectItem> => {
			return items.reduce<Array<IGroupSelectItem>>((filteredItems, item) => {
				if (item.items && item.items.length > 0) {
					const checkedSubItems = filterCheckedItems(item.items);

					if (checkedSubItems.length > 0) {
						filteredItems.push({
							...item,
							items: checkedSubItems,
						});
					}
				} else if (item.checked) {
					filteredItems.push({ ...item });
				}

				return filteredItems;
			}, []);
		};

		const checkedItems = filterCheckedItems(option.items);
		if (checkedItems.length > 0) {
			acc.push({
				category: option.category,
				items: checkedItems,
			});
		}

		return acc;
	}, []);

export const countItems = (items: Array<GroupSelectItemInterface>): number => {
	return items.reduce((count, item) => {
		if (item.items && item.items.length > 0) {
			return count + countItems(item.items);
		}

		return count + 1;
	}, 0);
};
